'use client'

import { useState } from 'react'
import { VideoCallInterface } from '@/components/video-call/VideoCallInterface'
import { TSODashboard } from '@/components/video-call/TSODashboard'
import { VideoCallLanding } from '@/components/video-call/VideoCallLanding'
import { useSearchParams } from 'next/navigation'

interface RoomData {
  token: string
  roomName: string
  wsUrl: string
}

export function VideoCallPageContent() {
  const searchParams = useSearchParams()
  const viewParam = searchParams.get('view')
  
  const [isCallActive, setIsCallActive] = useState(false)
  const [participantName, setParticipantName] = useState('')
  const [dealerLocation, setDealerLocation] = useState('')
  const [roomData, setRoomData] = useState<RoomData | null>(null)
  const [anamSessionToken, setAnamSessionToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  // Check if TSO dashboard view
  if (viewParam === 'tso') {
    return <TSODashboard />
  }

  const startCall = async () => {
    if (!participantName.trim()) {
      setError('Please enter your name')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Create LiveKit room
      const roomResponse = await fetch('/api/video-call/create-room', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          participantName: participantName.trim(),
        }),
      })

      if (!roomResponse.ok) {
        throw new Error('Failed to create room')
      }

      const roomData = await roomResponse.json()
      
      // Create Anam session token
      const anamResponse = await fetch('/api/video-call/create-anam-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomName: roomData.roomName,
          dealerName: participantName.trim(),
          dealerLocation: dealerLocation.trim() || 'Unknown',
        }),
      })

      let anamToken = null
      if (anamResponse.ok) {
        const anamData = await anamResponse.json()
        anamToken = anamData.sessionToken || anamData.session_token
      } else if (anamResponse.status === 429) {
        setError('Concurrency limit reached. Please close other browser tabs with active calls and try again.')
        setIsLoading(false)
        return
      }

      setRoomData(roomData)
      setAnamSessionToken(anamToken)
      setIsCallActive(true)
    } catch (error) {
      console.error('Error starting call:', error)
      setError('Failed to start call. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const endCall = () => {
    setIsCallActive(false)
    setRoomData(null)
    setAnamSessionToken(null)
  }

  if (isCallActive && roomData) {
    return (
      <VideoCallInterface
        roomData={roomData}
        onEndCall={endCall}
        participantName={participantName}
        dealerLocation={dealerLocation}
        anamSessionToken={anamSessionToken}
      />
    )
  }

  return (
    <VideoCallLanding
      participantName={participantName}
      setParticipantName={setParticipantName}
      dealerLocation={dealerLocation}
      setDealerLocation={setDealerLocation}
      onStartCall={startCall}
      isLoading={isLoading}
      error={error}
      setError={setError}
    />
  )
}
